import {isEnvConfEnabled} from '../utils/env/isEnvConfEnabled';
import {ENV_CONFIG_VARIABLES} from '../constants/env';
import {AD_GOALS} from './adGoals';

/**
 * ПИКСЕЛЬ VK РЕКЛАМЫ (счетчик Top.Mail.Ru).
 *
 * Сам счетчик подключается инлайн-снипетом в public/index.html — он создает
 * очередь window._tmr и подтягивает библиотеку. Здесь только отправка событий:
 *
 *  - pageView — «просмотр страницы». Мы SPA с одним index.html и без смены
 *    URL, поэтому настоящий pageView случается ровно один раз за загрузку.
 *    Экраны шлют виртуальные просмотры сами (см. usePageAnalytics);
 *  - reachGoal — целевое событие. Именно по целям VK строит аудитории
 *    ретаргетинга и обучает автостратегии, поэтому цели расставлены руками:
 *    «Автоматический поиск событий» в кабинете у нас почти ничего не найдет —
 *    форм нет, URL не меняется, а игровые действия происходят на канве Phaser,
 *    кликов по DOM там не существует.
 *
 * Цели (см. adGoals.js) должны быть заведены в кабинете VK Рекламы с теми же
 * именами — иначе они видны в статистике, но недоступны для оптимизации.
 */

// Номер счетчика из кабинета VK Рекламы (он же в снипете в public/index.html)
export const VK_PIXEL_COUNTER_ID = '3791079';

// Имена целей общие с Яндекс Метрикой (см. adGoals.js) — псевдоним оставлен
// для обратной совместимости
export const VK_GOALS = AD_GOALS;

const checkIfEnabled = () => {
  if (isEnvConfEnabled(ENV_CONFIG_VARIABLES.REACT_APP_DISABLE_VK_PIXEL)) {
    return false;
  }
  // Локальная разработка не должна попадать в статистику кампаний
  if (process.env.NODE_ENV !== 'production') {
    return false;
  }
  // Проверять очередь через Array.isArray нельзя: массивом window._tmr бывает
  // только до загрузки библиотеки. Сам code.js при старте подменяет его своим
  // объектом — у того тоже есть push(), но это уже не массив, и все
  // виртуальные просмотры и цели после загрузки счетчика молча терялись бы;
  // до кабинета доходил бы один pageView из снипета
  if (typeof window === 'undefined' || !window._tmr) {
    return false;
  }
  return typeof window._tmr.push === 'function';
};

/**
 * Виртуальный просмотр «страницы»: у нас это экран приложения.
 * @param {string} pageId - идентификатор экрана (см. usePageAnalytics)
 * @param {string} [userId] - id игрока для кросс-девайс матчинга
 */
export const trackVkPageView = (pageId, userId) => {
  if (!checkIfEnabled()) return;

  try {
    window._tmr.push({
      id: VK_PIXEL_COUNTER_ID,
      type: 'pageView',
      start: Date.now(),
      // Экраны URL не меняют — путь собираем сами, иначе в отчете все
      // просмотры сливаются в одну строку «/»
      url: `${window.location.origin}/${pageId}`,
      ...(userId ? {userid: userId} : {}),
    });
  } catch (error) {
    console.error('[VkPixel] pageView failed:', error);
  }
};

/**
 * Целевое событие.
 * @param {string} goal - имя цели из VK_GOALS (должно совпадать с кабинетом)
 * @param {Object} [options]
 * @param {string} [options.userId] - id игрока
 * @param {number} [options.value] - ценность события (для отчетов)
 * @param {Object} [options.params] - произвольные параметры события
 */
export const trackVkGoal = (goal, {userId, value, params} = {}) => {
  if (!checkIfEnabled() || !goal) return;

  try {
    window._tmr.push({
      id: VK_PIXEL_COUNTER_ID,
      type: 'reachGoal',
      goal,
      ...(userId ? {userid: userId} : {}),
      ...(value !== undefined ? {value} : {}),
      ...(params ? {params} : {}),
    });
  } catch (error) {
    console.error('[VkPixel] reachGoal failed:', error);
  }
};
